/**
 * Word Clash AI
 *
 * AI opponent with three difficulty levels for the Word Clash game.
 * - Easy: Shorter words, no premium square awareness
 * - Medium: Medium-length words, some premium targeting
 * - Hard: Longest/highest-scoring words, premium square targeting, rack leave strategy
 */

import {
  Tile,
  BoardCell,
  WordGameState,
  WordGameDifficulty,
  TilePlacement,
  Direction,
  AIMove,
  PremiumSquareType,
} from './types';
import { isValidWord, getWordsFromLetters } from './dictionary';

const BOARD_SIZE = 15;

export class WordGameAI {
  private difficulty: WordGameDifficulty;

  constructor(difficulty: WordGameDifficulty) {
    this.difficulty = difficulty;
  }

  /**
   * Calculate the best move for the AI given the current game state.
   */
  findBestMove(state: WordGameState): AIMove {
    const player = state.players[state.currentPlayerIndex];
    const rack = player.rack;

    // Find all valid placements
    const validMoves = this.findAllValidMoves(state, rack);

    if (validMoves.length === 0) {
      // No valid moves - exchange or pass
      if (state.tileBag.length >= 7) {
        return this.getExchangeMove(rack);
      }
      return { type: 'pass' };
    }

    // Select move based on difficulty
    const selectedMove = this.selectMove(validMoves, rack);
    return selectedMove;
  }

  private selectMove(moves: ScoredMove[], rack: Tile[]): AIMove {
    // Sort by score descending
    const sorted = [...moves].sort((a, b) => b.score - a.score);

    switch (this.difficulty) {
      case 'easy':
        return this.selectEasyMove(sorted);
      case 'medium':
        return this.selectMediumMove(sorted);
      case 'hard':
        return this.selectHardMove(sorted, rack);
    }
  }

  private selectEasyMove(sorted: ScoredMove[]): AIMove {
    // Prefer shorter words (2-4 letters), pick from bottom 60% of scores
    const shortWords = sorted.filter(m => m.wordLength <= 4);
    const pool = shortWords.length > 0 ? shortWords : sorted;

    // Pick from the worse half with some randomness
    const lowerHalf = pool.slice(Math.floor(pool.length * 0.4));
    const pick = lowerHalf.length > 0
      ? lowerHalf[Math.floor(Math.random() * lowerHalf.length)]
      : pool[pool.length - 1];

    return {
      type: 'play',
      placement: {
        tiles: pick.placements,
        direction: pick.direction,
        startRow: pick.placements[0].row,
        startCol: pick.placements[0].col,
      },
    };
  }

  private selectMediumMove(sorted: ScoredMove[]): AIMove {
    // Prefer medium-length words (3-6 letters), some premium awareness
    const medWords = sorted.filter(m => m.wordLength >= 3 && m.wordLength <= 6);
    const pool = medWords.length > 0 ? medWords : sorted;

    // Pick from top 50%
    const topHalf = pool.slice(0, Math.max(1, Math.ceil(pool.length * 0.5)));
    const pick = topHalf[Math.floor(Math.random() * topHalf.length)];

    return {
      type: 'play',
      placement: {
        tiles: pick.placements,
        direction: pick.direction,
        startRow: pick.placements[0].row,
        startCol: pick.placements[0].col,
      },
    };
  }

  private selectHardMove(sorted: ScoredMove[], rack: Tile[]): AIMove {
    // Best scoring move, with rack leave consideration
    // Consider top 5 moves and pick the one with best combined score + rack leave
    const top = sorted.slice(0, Math.min(5, sorted.length));

    let bestMove = top[0];
    let bestValue = -Infinity;

    for (const move of top) {
      const rackLeaveValue = this.evaluateRackLeave(rack, move.placements);
      const combinedValue = move.score + rackLeaveValue * 0.3;
      if (combinedValue > bestValue) {
        bestValue = combinedValue;
        bestMove = move;
      }
    }

    return {
      type: 'play',
      placement: {
        tiles: bestMove.placements,
        direction: bestMove.direction,
        startRow: bestMove.placements[0].row,
        startCol: bestMove.placements[0].col,
      },
    };
  }

  private evaluateRackLeave(rack: Tile[], usedPlacements: TilePlacement[]): number {
    const usedIds = new Set(usedPlacements.map(p => p.tile.id));
    const remaining = rack.filter(t => !usedIds.has(t.id));

    let value = 0;
    const vowels = 'AEIOU';
    const goodConsonants = 'RSTLNE';

    let vowelCount = 0;
    let consonantCount = 0;

    for (const tile of remaining) {
      const letter = tile.letter.toUpperCase();
      if (vowels.includes(letter)) vowelCount++;
      else consonantCount++;

      // Good tiles to keep
      if (goodConsonants.includes(letter)) value += 2;
      if (tile.isBlank) value += 10;
      if ('S' === letter) value += 5;

      // Bad tiles to keep
      if ('QUV'.includes(letter)) value -= 3;
      if (tile.value >= 8) value -= 2;
    }

    // Balance vowels and consonants
    if (remaining.length > 0) {
      const ratio = vowelCount / remaining.length;
      if (ratio >= 0.3 && ratio <= 0.5) value += 3;
    }

    return value;
  }

  private getExchangeMove(rack: Tile[]): AIMove {
    // Exchange worst tiles (high value, difficult letters)
    const sorted = [...rack].sort((a, b) => {
      // Prefer to exchange Q without U, high-value tiles
      const aScore = a.value + (a.letter === 'Q' ? 5 : 0) + (a.letter === 'V' ? 2 : 0);
      const bScore = b.value + (b.letter === 'Q' ? 5 : 0) + (b.letter === 'V' ? 2 : 0);
      return bScore - aScore;
    });

    // Exchange up to 3 worst tiles
    const toExchange = sorted.slice(0, Math.min(3, sorted.length));
    return {
      type: 'exchange',
      exchangeTiles: toExchange,
    };
  }

  // ==========================================================================
  // Move Finding
  // ==========================================================================

  private findAllValidMoves(state: WordGameState, rack: Tile[]): ScoredMove[] {
    const moves: ScoredMove[] = [];
    const board = state.board;
    const isBoardEmpty = this.checkBoardEmpty(board);

    if (isBoardEmpty) {
      // First move: must cross center
      this.findFirstMoves(board, rack, moves);
    } else {
      // Find anchor squares (empty squares adjacent to filled squares)
      const anchors = this.findAnchors(board);
      for (const anchor of anchors) {
        this.findMovesAtAnchor(board, rack, anchor.row, anchor.col, moves);
      }
    }

    // Limit moves for performance
    return moves.slice(0, 200);
  }

  private checkBoardEmpty(board: BoardCell[][]): boolean {
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (board[r][c].tile) return false;
      }
    }
    return true;
  }

  private findFirstMoves(board: BoardCell[][], rack: Tile[], moves: ScoredMove[]): void {
    const letters = rack.map(t => t.isBlank ? '' : t.letter.toLowerCase());
    const words = getWordsFromLetters(letters, 2, Math.min(7, rack.length));

    for (const word of words.slice(0, 50)) { // Limit for performance
      // Try placing horizontally through center
      for (let startCol = Math.max(0, 7 - word.length + 1); startCol <= 7; startCol++) {
        if (startCol + word.length > BOARD_SIZE) continue;
        const placements = this.buildPlacements(word, 7, startCol, 'horizontal', rack, board);
        if (placements) {
          const score = this.calculatePlacementScore(placements, board);
          moves.push({
            placements,
            direction: 'horizontal',
            score,
            wordLength: word.length,
          });
        }
      }

      // Try placing vertically through center
      for (let startRow = Math.max(0, 7 - word.length + 1); startRow <= 7; startRow++) {
        if (startRow + word.length > BOARD_SIZE) continue;
        const placements = this.buildPlacements(word, startRow, 7, 'vertical', rack, board);
        if (placements) {
          const score = this.calculatePlacementScore(placements, board);
          moves.push({
            placements,
            direction: 'vertical',
            score,
            wordLength: word.length,
          });
        }
      }
    }
  }

  private findAnchors(board: BoardCell[][]): { row: number; col: number }[] {
    const anchors: { row: number; col: number }[] = [];
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (board[r][c].tile) continue;
        const neighbors = [
          [r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1],
        ];
        const hasNeighbor = neighbors.some(([nr, nc]) =>
          nr >= 0 && nr < BOARD_SIZE && nc >= 0 && nc < BOARD_SIZE && board[nr][nc].tile
        );
        if (hasNeighbor) {
          anchors.push({ row: r, col: c });
        }
      }
    }
    return anchors;
  }

  private findMovesAtAnchor(board: BoardCell[][], rack: Tile[], row: number, col: number, moves: ScoredMove[]): void {
    const letters = rack.map(t => t.isBlank ? '' : t.letter.toLowerCase());
    const words = getWordsFromLetters(letters, 2, Math.min(7, rack.length));

    // Limit search for performance
    const wordSubset = words.slice(0, 30);

    for (const word of wordSubset) {
      // Try horizontal placement crossing this anchor
      for (let startCol = Math.max(0, col - word.length + 1); startCol <= col; startCol++) {
        if (startCol + word.length > BOARD_SIZE) continue;
        const placements = this.tryWordPlacement(word, row, startCol, 'horizontal', rack, board);
        if (placements && placements.length > 0) {
          // Verify it forms valid cross words
          if (this.validateCrossWords(placements, board)) {
            const score = this.calculatePlacementScore(placements, board);
            moves.push({ placements, direction: 'horizontal', score, wordLength: word.length });
          }
        }
      }

      // Try vertical placement crossing this anchor
      for (let startRow = Math.max(0, row - word.length + 1); startRow <= row; startRow++) {
        if (startRow + word.length > BOARD_SIZE) continue;
        const placements = this.tryWordPlacement(word, startRow, col, 'vertical', rack, board);
        if (placements && placements.length > 0) {
          if (this.validateCrossWords(placements, board)) {
            const score = this.calculatePlacementScore(placements, board);
            moves.push({ placements, direction: 'vertical', score, wordLength: word.length });
          }
        }
      }
    }
  }

  private tryWordPlacement(word: string, startRow: number, startCol: number, direction: Direction, rack: Tile[], board: BoardCell[][]): TilePlacement[] | null {
    const placements: TilePlacement[] = [];
    const rackCopy = [...rack];
    let connectsToExisting = false;

    for (let i = 0; i < word.length; i++) {
      const r = direction === 'horizontal' ? startRow : startRow + i;
      const c = direction === 'horizontal' ? startCol + i : startCol;

      if (r >= BOARD_SIZE || c >= BOARD_SIZE) return null;

      const existingTile = board[r][c].tile;
      if (existingTile) {
        // Cell already has a tile - must match
        const existingLetter = existingTile.isBlank ? existingTile.letter.toLowerCase() : existingTile.letter.toLowerCase();
        if (existingLetter !== word[i]) return null;
        connectsToExisting = true;
        continue; // Don't need to place a tile here
      }

      // Need to place a tile from rack
      const tileIdx = rackCopy.findIndex(t => t.letter.toLowerCase() === word[i] && !t.isBlank);
      if (tileIdx !== -1) {
        const tile = rackCopy[tileIdx];
        rackCopy.splice(tileIdx, 1);
        placements.push({ tile, row: r, col: c });
      } else {
        // Try blank tile
        const blankIdx = rackCopy.findIndex(t => t.isBlank);
        if (blankIdx !== -1) {
          const blankTile = { ...rackCopy[blankIdx], letter: word[i].toUpperCase() };
          rackCopy.splice(blankIdx, 1);
          placements.push({ tile: blankTile, row: r, col: c });
        } else {
          return null; // Can't form this word
        }
      }
    }

    if (placements.length === 0) return null;

    // Check adjacency to existing tiles
    if (!connectsToExisting) {
      const hasAdjacentExisting = placements.some(p => {
        const neighbors = [[p.row - 1, p.col], [p.row + 1, p.col], [p.row, p.col - 1], [p.row, p.col + 1]];
        return neighbors.some(([nr, nc]) =>
          nr >= 0 && nr < BOARD_SIZE && nc >= 0 && nc < BOARD_SIZE && board[nr][nc].tile
        );
      });
      if (!hasAdjacentExisting) return null;
    }

    return placements;
  }

  private buildPlacements(word: string, startRow: number, startCol: number, direction: Direction, rack: Tile[], board: BoardCell[][]): TilePlacement[] | null {
    const placements: TilePlacement[] = [];
    const rackCopy = [...rack];

    for (let i = 0; i < word.length; i++) {
      const r = direction === 'horizontal' ? startRow : startRow + i;
      const c = direction === 'horizontal' ? startCol + i : startCol;

      if (r >= BOARD_SIZE || c >= BOARD_SIZE) return null;

      const tileIdx = rackCopy.findIndex(t => t.letter.toLowerCase() === word[i] && !t.isBlank);
      if (tileIdx !== -1) {
        const tile = rackCopy[tileIdx];
        rackCopy.splice(tileIdx, 1);
        placements.push({ tile, row: r, col: c });
      } else {
        const blankIdx = rackCopy.findIndex(t => t.isBlank);
        if (blankIdx !== -1) {
          const blankTile = { ...rackCopy[blankIdx], letter: word[i].toUpperCase() };
          rackCopy.splice(blankIdx, 1);
          placements.push({ tile: blankTile, row: r, col: c });
        } else {
          return null;
        }
      }
    }

    return placements;
  }

  private validateCrossWords(placements: TilePlacement[], board: BoardCell[][]): boolean {
    for (const p of placements) {
      // Check vertical cross word for horizontal placements and vice versa
      const directions: Direction[] = ['horizontal', 'vertical'];
      for (const dir of directions) {
        const word = this.extractCrossWord(p.row, p.col, dir, placements, board);
        if (word && word.length >= 2) {
          if (!isValidWord(word)) return false;
        }
      }
    }
    return true;
  }

  private extractCrossWord(row: number, col: number, direction: Direction, placements: TilePlacement[], board: BoardCell[][]): string | null {
    let r = row;
    let c = col;

    // Find start
    if (direction === 'horizontal') {
      while (c > 0 && (board[r][c - 1].tile || placements.some(p => p.row === r && p.col === c - 1))) c--;
    } else {
      while (r > 0 && (board[r - 1][c].tile || placements.some(p => p.row === r - 1 && p.col === c))) r--;
    }

    let word = '';
    let cr = r;
    let cc = c;
    while (cr < BOARD_SIZE && cc < BOARD_SIZE) {
      const placement = placements.find(p => p.row === cr && p.col === cc);
      const existing = board[cr][cc].tile;
      if (placement) {
        word += placement.tile.letter.toLowerCase();
      } else if (existing) {
        word += existing.letter.toLowerCase();
      } else {
        break;
      }
      if (direction === 'horizontal') cc++;
      else cr++;
    }

    return word.length >= 2 ? word : null;
  }

  private calculatePlacementScore(placements: TilePlacement[], board: BoardCell[][]): number {
    let score = 0;
    let wordMultiplier = 1;

    for (const p of placements) {
      let letterScore = p.tile.value;
      const premium = board[p.row][p.col].premium;

      switch (premium) {
        case PremiumSquareType.DOUBLE_LETTER:
          letterScore *= 2;
          break;
        case PremiumSquareType.TRIPLE_LETTER:
          letterScore *= 3;
          break;
        case PremiumSquareType.DOUBLE_WORD:
        case PremiumSquareType.CENTER:
          wordMultiplier *= 2;
          break;
        case PremiumSquareType.TRIPLE_WORD:
          wordMultiplier *= 3;
          break;
      }

      score += letterScore;
    }

    return score * wordMultiplier;
  }
}

interface ScoredMove {
  placements: TilePlacement[];
  direction: Direction;
  score: number;
  wordLength: number;
}

export default WordGameAI;
